'use client';

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '@/components/Modal';
import { Search } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface CommandItem {
  id: string;
  label: string;
  description?: string;
  group: string;
  action: () => void;
}

export interface CommandPaletteProps {
  items: CommandItem[];
}

export function CommandPalette({ items }: CommandPaletteProps) {
  const { t } = useTranslation('common', { keyPrefix: 'commandPalette' });
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);

  const filtered = items.filter(
    (item) =>
      item.label.toLowerCase().includes(query.toLowerCase()) ||
      item.description?.toLowerCase().includes(query.toLowerCase()) ||
      item.group.toLowerCase().includes(query.toLowerCase())
  );

  const grouped = filtered.reduce<Record<string, CommandItem[]>>((acc, item) => {
    if (!acc[item.group]) acc[item.group] = [];
    (acc as Record<string, CommandItem[]>)[item.group]!.push(item);
    return acc;
  }, {});

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen((v) => !v);
        setQuery('');
        setActiveIndex(0);
      }
    };

    const handleOpenCommand = () => {
      setOpen(true);
      setQuery('');
      setActiveIndex(0);
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('open-command-palette', handleOpenCommand);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('open-command-palette', handleOpenCommand);
    };
  }, []);

  const handleKeyDown2 = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === 'Enter' && filtered[activeIndex]) {
        filtered[activeIndex].action();
        setOpen(false);
      } else if (e.key === 'Escape') {
        setOpen(false);
      }
    },
    [filtered, activeIndex]
  );

  if (!open) return null;

  return (
    <Modal isOpen={open} onClose={() => setOpen(false)} title="">
      <div className="space-y-2" onKeyDown={handleKeyDown2}>
        {/* Search input */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActiveIndex(0);
            }}
            placeholder={t('placeholder')}
            className="w-full h-11 pl-9 pr-4 rounded-lg border border-border bg-surface-0 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition"
            autoFocus
          />
        </div>

        {/* Results */}
        <div className="max-h-64 overflow-y-auto space-y-3">
          {Object.entries(grouped).map(([group, groupItems]) => (
            <div key={group}>
              <p className="text-caption text-muted-foreground uppercase tracking-wider mb-1">{group}</p>
              {groupItems.map((item) => {
                const flatIndex = filtered.indexOf(item);
                return (
                  <button
                    key={item.id}
                    onClick={() => {
                      item.action();
                      setOpen(false);
                    }}
                    className={cn(
                      'w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors',
                      flatIndex === activeIndex
                        ? 'bg-surface-2 text-foreground'
                        : 'text-muted-foreground hover:bg-surface-1'
                    )}
                    onMouseEnter={() => setActiveIndex(flatIndex)}
                  >
                    <span className="font-medium">{item.label}</span>
                    {item.description && (
                      <span className="text-xs text-muted-foreground truncate">{item.description}</span>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
          {filtered.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-6">{t('noResults')}</p>
          )}
        </div>

        <p className="text-caption text-muted-foreground text-center">
          {t('hint')}
        </p>
      </div>
    </Modal>
  );
}
